"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.REQUEST_PART_LABELS = exports.REQUEST_PARTS = exports.REQUEST_COMPLETION_LABELS = exports.REQUEST_COMPLETION_PARTS = exports.requestJobDetailsSchema = exports.requestIdealCandidateSchema = exports.IDEAL_CANDIDATE_PART_MAX = exports.IDEAL_CANDIDATE_MIN_WORDS = exports.ICP_REQUEST_DRAFT_COST_RANGE_LABEL = exports.IDEAL_CANDIDATE_PROMPTS = exports.IDEAL_CANDIDATE_PARTS = exports.requestBriefSchema = exports.REQUEST_BRIEF_KEYS = void 0;
exports.requestBriefTitle = requestBriefTitle;
exports.requestBriefOf = requestBriefOf;
exports.briefBulletsOf = briefBulletsOf;
exports.justificationOf = justificationOf;
exports.bulletsFromLines = bulletsFromLines;
exports.emptyIdealCandidate = emptyIdealCandidate;
exports.idealCandidateOf = idealCandidateOf;
exports.wordCountOf = wordCountOf;
exports.idealCandidateWords = idealCandidateWords;
exports.idealCandidatePercent = idealCandidatePercent;
exports.hasIdealCandidate = hasIdealCandidate;
exports.idealCandidateText = idealCandidateText;
exports.emptyJobDetails = emptyJobDetails;
exports.jobDetailsOf = jobDetailsOf;
exports.shiftProblem = shiftProblem;
exports.requestCompletion = requestCompletion;
exports.isRequestPart = isRequestPart;
exports.editMarksOf = editMarksOf;
exports.maskedEditMarks = maskedEditMarks;
exports.changedParts = changedParts;
exports.changedLines = changedLines;
const zod_1 = require("zod");
const enums_1 = require("./enums");
const calendars_1 = require("./calendars");
const schemas_1 = require("./schemas");
const permissions_1 = require("./permissions");
const job_builder_1 = require("./job-builder");
const hiring_templates_1 = require("./hiring-templates");
// ADR 0075 (workflow review 2026-10-29 section B; DECISIONS-LOG 2026-10-29 "Section B" Q1–Q4 and 2026-11-01 Q1):
// what the requester writes on a hire request besides the hire itself. The job's Basics except the recruiter, the
// responsibilities, the brief's four parts and a guided ideal-candidate text. All of it is copied into the draft job made
// on the last approval. HR may edit any of it at the HR check; every edit by someone other than the requester is kept,
// with what the requester first wrote, and shows as an "Edited by HR" mark.
// --- The brief: the job's four starting parts, nothing more (Q2 a; B1-a: no 60-day plan) ---------------
exports.REQUEST_BRIEF_KEYS = ['why', 'achieve', 'plan_30', 'plan_90'];
const BRIEF_TITLES = Object.fromEntries(job_builder_1.DEFAULT_BRIEF_SECTIONS.map((section) => [section.key, section.title]));
/** "Why we need this role" and the others, in the job's own words, so the request and the job never disagree. */
function requestBriefTitle(key) {
    return BRIEF_TITLES[key] ?? key;
}
exports.requestBriefSchema = zod_1.z
    .array(job_builder_1.briefSectionSchema)
    .max(exports.REQUEST_BRIEF_KEYS.length)
    .refine((sections) => sections.every((section) => exports.REQUEST_BRIEF_KEYS.some((key) => key === section.key)), 'The brief has four parts.')
    .refine((sections) => new Set(sections.map((section) => section.key)).size === sections.length, 'Two parts share a reference.');
/** The four parts in order, whatever was stored: missing parts are empty and empty points are dropped. */
function requestBriefOf(stored) {
    const parsed = zod_1.z.array(job_builder_1.briefSectionSchema).safeParse(stored);
    const sections = parsed.success ? parsed.data : [];
    return exports.REQUEST_BRIEF_KEYS.map((key) => ({
        key,
        title: requestBriefTitle(key),
        bullets: (sections.find((section) => section.key === key)?.bullets ?? []).map((bullet) => bullet.trim()).filter((bullet) => bullet !== ''),
    }));
}
function briefBulletsOf(brief, key) {
    return brief.find((section) => section.key === key)?.bullets.filter((bullet) => bullet.trim() !== '') ?? [];
}
/**
 * "Why we need this role" is the request's old "Why this hire is needed" (`justification`), now as points. The server keeps
 * `justification` as these points, one per line, so the approval screens and saved versions that read it still do.
 */
function justificationOf(brief) {
    const text = briefBulletsOf(brief, 'why').join('\n').trim();
    return text === '' ? null : text;
}
/** One point per line in the box; blank lines are not points. */
function bulletsFromLines(text) {
    return text.split(/\r?\n/).map((line) => line.replace(/^\s*[-•*]\s*/, '').trim()).filter((line) => line !== '');
}
// --- The ideal candidate: a guided text with four prompts (Q1 a) --------------------------------------------
// ADR 0083: replaced on the form by the Ideal candidate profile template (`hiring-templates.ts`). A request or job saved with
// the four-part text keeps it: it is shown read-only as "written before the template" and still goes to Jev.
exports.IDEAL_CANDIDATE_PARTS = ['mustHaves', 'niceToHaves', 'experience', 'workHabits'];
exports.IDEAL_CANDIDATE_PROMPTS = {
    mustHaves: { label: 'Must-haves', prompt: 'What must they already have or be able to do from day one?' },
    niceToHaves: { label: 'Nice-to-haves', prompt: 'What would make them stand out, but you could teach?' },
    experience: { label: 'Experience', prompt: 'What work have they done before? How many years, and where?' },
    workHabits: { label: 'Work habits', prompt: 'How do they work day to day: pace, hours, alone or in a team, how they keep people updated?' },
};
/**
 * What turning the text into rated lines costs: one Sonnet draft, no questions (a few thousand tokens in, about 1,500 out). Shown
 * on the button the way the interview's "about 5–10¢" is (ADR 0070 stage 2).
 */
exports.ICP_REQUEST_DRAFT_COST_RANGE_LABEL = '2–5¢';
/** The workflow review's minimum standard: the bar is full at 100 words. */
exports.IDEAL_CANDIDATE_MIN_WORDS = 100;
exports.IDEAL_CANDIDATE_PART_MAX = 2000;
const idealPartSchema = zod_1.z.string().trim().max(exports.IDEAL_CANDIDATE_PART_MAX, `Keep each part under ${exports.IDEAL_CANDIDATE_PART_MAX} characters.`).default('');
exports.requestIdealCandidateSchema = zod_1.z.object({
    mustHaves: idealPartSchema,
    niceToHaves: idealPartSchema,
    experience: idealPartSchema,
    workHabits: idealPartSchema,
});
function emptyIdealCandidate() {
    return { mustHaves: '', niceToHaves: '', experience: '', workHabits: '' };
}
/** Whatever was stored, as the four parts (anything unreadable is empty). */
function idealCandidateOf(stored) {
    const parsed = exports.requestIdealCandidateSchema.safeParse(stored ?? {});
    return parsed.success ? parsed.data : emptyIdealCandidate();
}
/** Words are runs of letters or digits, so "-" or "•" on their own never count. */
function wordCountOf(text) {
    return text.split(/\s+/).filter((word) => /[\p{L}\p{N}]/u.test(word)).length;
}
function idealCandidateWords(note) {
    return exports.IDEAL_CANDIDATE_PARTS.reduce((total, part) => total + wordCountOf(note[part]), 0);
}
/** 0 to 100: how far the text is to the 100-word minimum. */
function idealCandidatePercent(note) {
    return Math.min(100, Math.floor((idealCandidateWords(note) / exports.IDEAL_CANDIDATE_MIN_WORDS) * 100));
}
function hasIdealCandidate(note) {
    return idealCandidateWords(note) > 0;
}
/** The text as one block with its four headings, for reading it back and for the AI. Empty parts are left out. */
function idealCandidateText(note) {
    return exports.IDEAL_CANDIDATE_PARTS.filter((part) => note[part].trim() !== '')
        .map((part) => `${exports.IDEAL_CANDIDATE_PROMPTS[part].label}: ${note[part].trim()}`)
        .join('\n\n');
}
// --- The job's Basics the requester fills (2026-11-01 Q1 a: every field except the recruiter) -----------
const detailText = (max) => zod_1.z.string().trim().max(max).nullable().default(null);
exports.requestJobDetailsSchema = zod_1.z.object({
    /** Who the new hire reports to; gets the shortlist. A new request starts with the requester. */
    lineManagerUserId: schemas_1.uuidSchema.nullable().default(null),
    publicTitle: detailText(160),
    employmentType: zod_1.z.enum(enums_1.EMPLOYMENT_TYPES).nullable().default(null),
    workMode: zod_1.z.enum(enums_1.WORK_MODES).nullable().default(null),
    location: detailText(160),
    roleFamily: detailText(80),
    targetCloseDate: calendars_1.isoDateSchema.nullable().default(null),
    isConfidential: zod_1.z.boolean().default(false),
    shiftStart: calendars_1.timeOfDaySchema.nullable().default(null),
    shiftEnd: calendars_1.timeOfDaySchema.nullable().default(null),
    shiftDisplayZone: calendars_1.timezoneSchema.nullable().default(null),
    shiftHours: detailText(200),
    /** The advert: about the job, and what candidates need. */
    description: detailText(20000),
    requirements: detailText(20000),
});
function emptyJobDetails() {
    return exports.requestJobDetailsSchema.parse({});
}
/** Whatever was stored, as the full set (anything unreadable is empty). */
function jobDetailsOf(stored) {
    const parsed = exports.requestJobDetailsSchema.safeParse(stored ?? {});
    return parsed.success ? parsed.data : emptyJobDetails();
}
/** The job's own rule (JOB-13): a shift that starts must also end and say the client's time zone. */
function shiftProblem(details) {
    if (details.shiftStart === null)
        return null;
    return details.shiftEnd === null || details.shiftDisplayZone === null ? 'Choose when the shift ends and the client’s time zone.' : null;
}
exports.REQUEST_COMPLETION_PARTS = [
    'place',
    'why',
    'responsibilities',
    'start',
    'achieve',
    'plan_30',
    'plan_90',
    'idealCandidate',
    'budget',
    'location',
    'description',
    'requirements',
];
exports.REQUEST_COMPLETION_LABELS = {
    place: 'Which position',
    why: 'Why we need this role',
    responsibilities: 'Responsibilities',
    start: 'Start date',
    achieve: 'What this person will achieve',
    plan_30: 'Onboarding plan, first 30 days',
    plan_90: 'Onboarding plan, first 90 days',
    idealCandidate: 'Ideal candidate',
    budget: 'Salary budget',
    location: 'Where they work',
    description: 'About the job',
    requirements: 'Requirements',
};
/** Must be filled before sending (Q3 a, plus the position and start date section A already required). */
const REQUIRED_PARTS = ['place', 'why', 'responsibilities', 'start'];
function filled(text) {
    return text !== null && text.trim() !== '';
}
/** Every part counts the same; the ideal candidate and the plans count by their required boxes (ADR 0083). */
function requestCompletion(input) {
    const shares = {
        place: input.hasPlace ? 1 : 0,
        why: briefBulletsOf(input.brief, 'why').length > 0 ? 1 : 0,
        responsibilities: filled(input.responsibilities) ? 1 : 0,
        start: input.hasStart ? 1 : 0,
        achieve: briefBulletsOf(input.brief, 'achieve').length > 0 ? 1 : 0,
        plan_30: (0, hiring_templates_1.progressShare)((0, hiring_templates_1.plan30Progress)(input.plan30, input.managesPeople)),
        plan_90: (0, hiring_templates_1.progressShare)((0, hiring_templates_1.plan90Progress)(input.plan90, input.managesPeople)),
        idealCandidate: (0, hiring_templates_1.progressShare)((0, hiring_templates_1.idealProfileProgress)(input.idealProfile, input.managesPeople)),
        budget: input.hasBudget ? 1 : 0,
        // A remote job has no place to name.
        location: filled(input.details.location) || input.details.workMode === 'remote' ? 1 : 0,
        description: filled(input.details.description) ? 1 : 0,
        requirements: filled(input.details.requirements) ? 1 : 0,
    };
    const items = exports.REQUEST_COMPLETION_PARTS.map((part) => ({ part, label: exports.REQUEST_COMPLETION_LABELS[part], share: shares[part], isRequired: REQUIRED_PARTS.includes(part) }));
    const total = items.reduce((sum, item) => sum + item.share, 0);
    return { percent: Math.floor((total / items.length) * 100), items };
}
// --- "Edited by HR" (Q4 a) ------------------------------------------------------------------------------------
/** Each part of a request someone may change, as the mark and the change list name it. */
exports.REQUEST_PARTS = [
    'title',
    'slotsRequested',
    'priority',
    'start',
    'budget',
    'jevBudget',
    'hire',
    'why',
    'responsibilities',
    'achieve',
    'plan_30',
    'plan_90',
    'idealCandidate',
    'lineManager',
    'hiringManager',
    'publicTitle',
    'employmentType',
    'workMode',
    'location',
    'roleFamily',
    'targetCloseDate',
    'isConfidential',
    'shift',
    'description',
    'requirements',
    'managesPeople',
];
exports.REQUEST_PART_LABELS = {
    title: 'Job title',
    slotsRequested: 'How many people',
    priority: 'How urgent',
    start: 'When they should start',
    budget: 'Salary budget',
    jevBudget: 'Jev budget',
    hire: 'Replacement or additional',
    why: 'Why we need this role',
    responsibilities: 'Responsibilities',
    achieve: 'What this person will achieve',
    plan_30: 'Onboarding plan, first 30 days',
    plan_90: 'Onboarding plan, first 90 days',
    idealCandidate: 'Ideal candidate',
    lineManager: 'Line manager',
    hiringManager: 'Hiring manager',
    publicTitle: 'Title candidates see',
    employmentType: 'Employment type',
    workMode: 'Work mode',
    location: 'Location',
    roleFamily: 'Role family',
    targetCloseDate: 'Target close date',
    isConfidential: 'Confidential',
    shift: 'Shift',
    description: 'About the job',
    requirements: 'Requirements',
    managesPeople: 'Manages people',
};
function isRequestPart(value) {
    return exports.REQUEST_PARTS.some((part) => part === value);
}
/** Edits in any order → one mark per part: the oldest edit's "before" is the requester's own words. */
function editMarksOf(edits) {
    const ordered = [...edits].sort((a, b) => a.editedAt.localeCompare(b.editedAt));
    const marks = {};
    for (const edit of ordered) {
        const earlier = marks[edit.part];
        marks[edit.part] = {
            part: edit.part,
            partLabel: edit.partLabel,
            originalText: earlier?.originalText ?? edit.beforeText,
            currentText: edit.afterText,
            editedByName: edit.editedByName,
            editedAt: edit.editedAt,
            editCount: (earlier?.editCount ?? 0) + 1,
        };
    }
    return marks;
}
/** The budget's mark, to someone who may not see salaries (§L23): both texts read "—", never left out (ADR 0091 D95). */
function maskedEditMarks(marks, canSeeBudget) {
    if (canSeeBudget || marks.budget === undefined)
        return marks;
    return { ...marks, budget: { ...marks.budget, originalText: permissions_1.RESTRICTED_FIELD_PLACEHOLDER, currentText: permissions_1.RESTRICTED_FIELD_PLACEHOLDER } };
}
/** Which parts read differently now. Both sides are the parts' texts, so a change is what a reader would see. */
function changedParts(before, after) {
    return exports.REQUEST_PARTS.filter((part) => before[part] !== after[part]);
}
/** Above this many line pairs the middle is shown as one change rather than compared line by line. */
const MAX_COMPARED_PAIRS = 250_000;
function linesOf(text) {
    return text
        .split(/\r?\n/)
        .map((line) => line.trimEnd())
        .filter((line) => line.trim() !== '');
}
/**
 * Only the lines that changed, as before → after, so "Years of experience: 5" → "Years of experience: 3" is not buried in two
 * whole texts. A plain comparison of lines (longest common run); blank lines are ignored. Nothing when the texts read the same.
 */
function changedLines(beforeText, afterText) {
    const before = linesOf(beforeText);
    const after = linesOf(afterText);
    let start = 0;
    while (start < before.length && start < after.length && before[start] === after[start])
        start += 1;
    let beforeEnd = before.length;
    let afterEnd = after.length;
    while (beforeEnd > start && afterEnd > start && before[beforeEnd - 1] === after[afterEnd - 1]) {
        beforeEnd -= 1;
        afterEnd -= 1;
    }
    const removed = before.slice(start, beforeEnd);
    const added = after.slice(start, afterEnd);
    if (removed.length === 0 && added.length === 0)
        return [];
    if (removed.length === 0 || added.length === 0 || removed.length * added.length > MAX_COMPARED_PAIRS)
        return [{ before: removed, after: added }];
    // common[i * width + j]: how many lines removed[i…] and added[j…] share, in order.
    const width = added.length + 1;
    const common = new Array((removed.length + 1) * width).fill(0);
    const at = (i, j) => common[i * width + j] ?? 0;
    for (let i = removed.length - 1; i >= 0; i -= 1) {
        for (let j = added.length - 1; j >= 0; j -= 1) {
            common[i * width + j] = removed[i] === added[j] ? at(i + 1, j + 1) + 1 : Math.max(at(i + 1, j), at(i, j + 1));
        }
    }
    const changes = [];
    let run = { before: [], after: [] };
    const close = () => {
        if (run.before.length > 0 || run.after.length > 0)
            changes.push(run);
        run = { before: [], after: [] };
    };
    let i = 0;
    let j = 0;
    while (i < removed.length || j < added.length) {
        const was = removed[i];
        const now = added[j];
        if (was !== undefined && now !== undefined && was === now) {
            close();
            i += 1;
            j += 1;
        }
        else if (now !== undefined && (was === undefined || at(i, j + 1) >= at(i + 1, j))) {
            run.after.push(now);
            j += 1;
        }
        else if (was !== undefined) {
            run.before.push(was);
            i += 1;
        }
    }
    close();
    return changes;
}
//# sourceMappingURL=request-content.js.map